import type { WerkflowInstanceSettings } from "@t3tools/contracts/settings";
import {
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
} from "@t3tools/client-runtime/state/runtime";
import { CheckCircle2Icon, PlusIcon, RadioIcon, Trash2Icon, XCircleIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { usePrimarySettings, useUpdatePrimarySettings } from "~/hooks/useSettings";
import { randomUUID } from "~/lib/utils";
import { usePrimaryEnvironmentId } from "~/state/environments";
import { serverEnvironment } from "~/state/server";
import { useAtomCommand } from "~/state/use-atom-command";
import { Button } from "../ui/button";
import { DraftInput } from "../ui/draft-input";
import { Switch } from "../ui/switch";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "./settingsLayout";

type TestState =
  | { readonly status: "idle" }
  | { readonly status: "running"; readonly instanceId: string }
  | { readonly status: "success"; readonly instanceId: string; readonly message: string }
  | { readonly status: "error"; readonly instanceId: string; readonly message: string };

function createDefaultInstance(): WerkflowInstanceSettings {
  return {
    id: `werkflow-${randomUUID().slice(0, 8)}`,
    label: "Aveda OS",
    apiUrl: "",
    apiKey: "",
    readOnlyApiKey: "",
    convexUrl: "",
    authToken: "",
    enabled: true,
  };
}

function isRunnableInstance(instance: WerkflowInstanceSettings): boolean {
  return instance.enabled && instance.apiUrl.trim().length > 0 && instance.apiKey.trim().length > 0;
}

export function WerkflowInstancesSettings() {
  const { werkflowInstances, activeWerkflowInstanceId } = usePrimarySettings((settings) => ({
    werkflowInstances: settings.werkflowInstances,
    activeWerkflowInstanceId: settings.activeWerkflowInstanceId,
  }));
  const updateSettings = useUpdatePrimarySettings();
  const environmentId = usePrimaryEnvironmentId();
  const testConnection = useAtomCommand(
    serverEnvironment.werkflowInstancesTestConnection,
    "werkflow instance test connection",
  );
  const [testState, setTestState] = useState<TestState>({ status: "idle" });
  const activeInstance = useMemo(
    () => werkflowInstances.find((instance) => instance.id === activeWerkflowInstanceId) ?? null,
    [activeWerkflowInstanceId, werkflowInstances],
  );

  const commitInstances = useCallback(
    (instances: ReadonlyArray<WerkflowInstanceSettings>, activeId = activeWerkflowInstanceId) => {
      updateSettings({
        werkflowInstances: [...instances],
        activeWerkflowInstanceId: activeId,
      });
    },
    [activeWerkflowInstanceId, updateSettings],
  );

  const addInstance = useCallback(() => {
    const next = createDefaultInstance();
    commitInstances([...werkflowInstances, next], activeWerkflowInstanceId ?? next.id);
  }, [activeWerkflowInstanceId, commitInstances, werkflowInstances]);

  const updateInstance = useCallback(
    (instanceId: string, patch: Partial<WerkflowInstanceSettings>) => {
      commitInstances(
        werkflowInstances.map((instance) =>
          instance.id === instanceId ? { ...instance, ...patch } : instance,
        ),
      );
    },
    [commitInstances, werkflowInstances],
  );

  const deleteInstance = useCallback(
    (instanceId: string) => {
      const nextInstances = werkflowInstances.filter((instance) => instance.id !== instanceId);
      const nextActiveId =
        activeWerkflowInstanceId === instanceId
          ? (nextInstances.find(isRunnableInstance)?.id ?? nextInstances[0]?.id ?? null)
          : activeWerkflowInstanceId;
      commitInstances(nextInstances, nextActiveId);
    },
    [activeWerkflowInstanceId, commitInstances, werkflowInstances],
  );

  const testInstance = useCallback(
    async (instance: WerkflowInstanceSettings) => {
      if (environmentId === null) return;
      setTestState({ status: "running", instanceId: instance.id });
      const result = await testConnection({
        environmentId,
        input: { instanceId: instance.id },
      });
      if (isAtomCommandInterrupted(result)) {
        setTestState({ status: "idle" });
        return;
      }
      if (result._tag === "Success") {
        const value = result.value;
        if (value.ok) {
          const parts = [
            "Verbunden",
            value.workspaceName ? `Workspace: ${value.workspaceName}` : null,
            value.keyLabel ? `Schlüssel: ${value.keyLabel}` : null,
            typeof value.scopeCount === "number" ? `${value.scopeCount} Scopes` : null,
            value.writeReady === true
              ? "vollständiger Schreibzugriff"
              : value.missingWriteScopes && value.missingWriteScopes.length > 0
                ? `fehlende Schreib-Scopes: ${value.missingWriteScopes.join(", ")}`
                : "Schreibzugriff nicht verifiziert",
          ].filter(Boolean);
          setTestState({
            status: "success",
            instanceId: instance.id,
            message: parts.join(" · "),
          });
        } else {
          setTestState({
            status: "error",
            instanceId: instance.id,
            message: value.error ?? "Verbindungstest fehlgeschlagen.",
          });
        }
        return;
      }
      const error = squashAtomCommandFailure(result);
      setTestState({
        status: "error",
        instanceId: instance.id,
        message: error instanceof Error ? error.message : "Verbindungstest fehlgeschlagen.",
      });
    },
    [environmentId, testConnection],
  );

  return (
    <SettingsPageContainer>
      <SettingsSection
        title="Aveda OS instances"
        headerAction={
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  size="icon-xs"
                  variant="ghost"
                  className="size-5 rounded-sm p-0 text-muted-foreground hover:text-foreground"
                  onClick={addInstance}
                  aria-label="ERP-Instanz hinzufügen"
                >
                  <PlusIcon className="size-3" />
                </Button>
              }
            />
            <TooltipPopup side="top">ERP-Instanz hinzufügen</TooltipPopup>
          </Tooltip>
        }
      >
        {werkflowInstances.length === 0 ? (
          <SettingsRow
            title="Keine ERP-Instanzen"
            description="Fügen Sie eine Aveda-OS-Instanz hinzu, bevor AvedaAgent auf ERP-Daten zugreifen kann."
            control={
              <Button size="sm" onClick={addInstance}>
                <PlusIcon className="size-4" />
                Instanz hinzufügen
              </Button>
            }
          />
        ) : (
          werkflowInstances.map((instance) => {
            const isActive = activeInstance?.id === instance.id;
            const currentTestState =
              testState.status !== "idle" && testState.instanceId === instance.id
                ? testState
                : null;
            return (
              <SettingsRow
                key={instance.id}
                title={
                  <span className="inline-flex items-center gap-2">
                    {instance.label}
                    {isActive ? (
                      <span className="rounded bg-foreground/8 px-1.5 py-0.5 font-medium text-[10px] text-muted-foreground">
                        Aktiv
                      </span>
                    ) : null}
                  </span>
                }
                description={instance.apiUrl.trim() ? instance.apiUrl : "ERP-API-URL erforderlich."}
                status={
                  currentTestState?.status === "success" ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600">
                      <CheckCircle2Icon className="size-3" />
                      {currentTestState.message}
                    </span>
                  ) : currentTestState?.status === "error" ? (
                    <span className="inline-flex items-center gap-1 text-destructive">
                      <XCircleIcon className="size-3" />
                      {currentTestState.message}
                    </span>
                  ) : null
                }
                control={
                  <>
                    <Switch
                      checked={instance.enabled}
                      onCheckedChange={(enabled) => updateInstance(instance.id, { enabled })}
                      aria-label={`${instance.label} aktivieren`}
                    />
                    <Button
                      size="icon-xs"
                      variant={isActive ? "default" : "outline"}
                      disabled={!isRunnableInstance(instance)}
                      onClick={() =>
                        updateSettings({
                          activeWerkflowInstanceId: instance.id,
                        })
                      }
                      aria-label={`${instance.label} als aktiv setzen`}
                    >
                      <RadioIcon className="size-3" />
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      disabled={
                        !isRunnableInstance(instance) ||
                        environmentId === null ||
                        currentTestState?.status === "running"
                      }
                      onClick={() => void testInstance(instance)}
                    >
                      {currentTestState?.status === "running" ? "Teste …" : "Verbindung testen"}
                    </Button>
                    <Button
                      size="icon-xs"
                      variant="ghost"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => deleteInstance(instance.id)}
                      aria-label={`${instance.label} löschen`}
                    >
                      <Trash2Icon className="size-3" />
                    </Button>
                  </>
                }
              >
                <div className="grid gap-3 border-t border-border/60 py-3.5 sm:grid-cols-2">
                  <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
                    Name
                    <DraftInput
                      value={instance.label}
                      placeholder="Aveda OS"
                      onCommit={(label) =>
                        updateInstance(instance.id, {
                          label: label.trim() || "Aveda OS",
                        })
                      }
                    />
                  </label>
                  <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
                    API-URL
                    <DraftInput
                      value={instance.apiUrl}
                      placeholder="https://your-deployment.convex.site"
                      onCommit={(apiUrl) => updateInstance(instance.id, { apiUrl })}
                    />
                  </label>
                  <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
                    Aveda-OS-API-Schlüssel
                    <DraftInput
                      type="password"
                      value={instance.apiKey}
                      placeholder="av_live_…"
                      onCommit={(apiKey) => updateInstance(instance.id, { apiKey })}
                    />
                  </label>
                  <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
                    Read-only-API-Schlüssel
                    <DraftInput
                      type="password"
                      value={instance.readOnlyApiKey}
                      placeholder="av_live_… (optional, für die CLI)"
                      onCommit={(readOnlyApiKey) => updateInstance(instance.id, { readOnlyApiKey })}
                    />
                  </label>
                </div>
              </SettingsRow>
            );
          })
        )}
      </SettingsSection>
    </SettingsPageContainer>
  );
}
